import { toast } from 'sonner';

import { attachSecret } from '@/lib/crm/secrets/service';
import { ApiError } from '@/lib/errors';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import type {
  GrantSelection,
  RpcPermissionGrant,
} from './api-access-grant-builders';
import { buildCredentialGrants } from './api-access-grant-builders';
import type { ReadScopings, WriteScopings } from './api-access-scopings';

// Secret minting lives in `lib/crm/secrets` because the record editor mints
// secrets too (the Secrets association row offers a custom create), and `lib/`
// cannot import from `pages/`. Re-exported here for this page's callers.
export type { GeneratedSecretResponse } from '@/lib/crm/secrets/service';
export { attachSecret, generateSecret } from '@/lib/crm/secrets/service';

/**
 * Thrown by `mintCredential` on failure, carrying the principal entity id so
 * a retry can reuse it instead of minting a duplicate orphaned entity.
 */
export class MintCredentialError extends Error {
  /**
   * Set ONLY once the entity-create step has actually succeeded — retry-reuse
   * depends on this being left `undefined` when the create step itself
   * failed, so a retry after a create-step failure correctly mints a fresh
   * entity instead of reusing an id that was never persisted.
   */
  principalEntityId?: string;
  /** The original error this wraps (e.g. an ApiError carrying a server
   * `detail`/`error`), preserved so failure copy can surface a
   * server-supplied reason. */
  readonly underlyingError?: unknown;

  constructor(
    message: string,
    principalEntityId?: string,
    underlyingError?: unknown
  ) {
    super(message);
    this.name = 'MintCredentialError';
    this.principalEntityId = principalEntityId;
    this.underlyingError = underlyingError;
  }
}

/**
 * Extract a server-supplied, human-readable failure reason from a caught
 * error, or `undefined` when none is safe to surface. Only an ApiError whose
 * message came from the response `detail`/`error` field qualifies; the
 * `HTTP <status>: <statusText>` fallback, transport failures
 * (NetworkError/TimeoutError), and generic errors all return undefined so the
 * caller shows the generic "Try again." sentence. A MintCredentialError is
 * unwrapped to the error it preserved.
 */
function serverReasonFromError(err: unknown): string | undefined {
  const underlying =
    err instanceof MintCredentialError ? err.underlyingError : err;
  if (!(underlying instanceof ApiError)) {
    return undefined;
  }
  const { response: resp } = underlying;
  if (resp && typeof resp === 'object') {
    const record = resp as Record<string, unknown>;
    const { detail } = record;
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
    const { error } = record;
    if (typeof error === 'string' && error.trim()) {
      return error.trim();
    }
  }
  return undefined;
}

/**
 * Pure formatter for grant-build / mint / save failures shared by the create
 * and edit slideouts. `kind` is the OPERATION ('create' vs 'edit') — the two
 * produce different copy
 * ("We couldn't create" vs "We couldn't update"; see docs/claude/ahoy-ux-copy
 * — "Failed to X"/"Could not X" is retired for customer-facing strings).
 * Returns structured output rather than calling `toast`/`logger` itself, so
 * callers stay responsible for the actual side effect and this stays
 * trivially testable.
 */
export function formatGrantBuildError(
  err: unknown,
  kind: 'create' | 'edit',
  credentialName: string
): { toastMessage: string; logPayload: Record<string, unknown> } {
  const verb = kind === 'create' ? 'create' : 'update';
  // Only a mint failure can know the entity id at format time (via
  // MintCredentialError, set once the create step succeeded); the edit path
  // always knows its credential's id up front and merges it in separately.
  const credentialEntityId =
    err instanceof MintCredentialError ? err.principalEntityId : undefined;
  const logPayload: Record<string, unknown> = {
    component: 'crm_events',
    credential_name: credentialName,
    ...(credentialEntityId ? { credential_entity_id: credentialEntityId } : {}),
  };
  const reason = serverReasonFromError(err);
  return {
    toastMessage: `We couldn't ${verb} this service account. ${reason ?? 'Try again.'}`,
    logPayload,
  };
}

/**
 * Builds a credential's grants and handles every failure mode the same way
 * across the create and edit slideouts: format the error, log it (with `kind`
 * as a structured field so create/edit failures stay distinguishable in
 * consolidated logs), and show the toast. Returns `undefined` on failure —
 * the error has already been surfaced, so callers just bail out.
 *
 * Replaces the identical try/catch that both slideouts wrapped around
 * `buildCredentialGrants` with, differing only in `kind` and the
 * credential-name source.
 */
export function buildGrantsWithErrorHandling(
  kind: 'create' | 'edit',
  credentialName: string,
  selections: GrantSelection[],
  scopingsByType: ReadonlyMap<string, ReadScopings>,
  writeScopingsByType: ReadonlyMap<string, WriteScopings[]>,
  userEntityId: string | null,
  orgEntityId: string | null,
  orgId: number,
  credentialEntityId?: string
): RpcPermissionGrant[] | undefined {
  try {
    return buildCredentialGrants(
      selections,
      scopingsByType,
      writeScopingsByType,
      userEntityId,
      orgEntityId
    );
  } catch (err) {
    const { toastMessage, logPayload } = formatGrantBuildError(
      err,
      kind,
      credentialName
    );
    logger.error(
      'Failed to build credential grants',
      err instanceof Error ? err : new Error(String(err)),
      {
        ...logPayload,
        kind,
        org_id: orgId,
        ...(credentialEntityId
          ? { credential_entity_id: credentialEntityId }
          : {}),
      }
    );
    toast.error(toastMessage);
    return undefined;
  }
}

export interface MintCredentialParams {
  orgId: number;
  userId: number;
  displayName: string;
  /** The picker selections; the server builds the grants from these; the grants
   * it stores are bounded by what the creator can read. */
  selections: GrantSelection[];
  /**
   * Reuse this entity id instead of minting a new one — set when retrying
   * after a partial-mint failure (entity created, secret attach failed), so
   * the retry attaches a secret to the SAME entity instead of creating a
   * second orphaned one. Leave unset for a first attempt.
   */
  existingPrincipalEntityId?: string;
}

interface ServiceAccountSelectionWire {
  entity_type_name: string;
  include_private: boolean;
  include_write: boolean;
}

interface ServiceAccountMintResponse {
  principal_entity_id: string;
}

/**
 * Mint a service account. The principal is created server-side by
 * `POST /api/orgs/{org}/api-access/service-accounts/`, which also stamps the
 * creator association, so no events are submitted from here. Retry semantics:
 * a create that succeeded but whose attach failed carries its entity id on the
 * error so the retry attaches to the SAME principal instead of minting a second
 * one.
 */
export async function mintCredential({
  orgId,
  userId,
  displayName,
  selections,
  existingPrincipalEntityId,
}: MintCredentialParams): Promise<{
  principalEntityId: string;
  secretEntityId: string;
  token: string;
  hint: string;
  masked_hint: string;
}> {
  let principalEntityId = existingPrincipalEntityId;
  if (!principalEntityId) {
    const wireSelections: ServiceAccountSelectionWire[] = selections.map(s => ({
      entity_type_name: s.entityTypeName,
      include_private: s.includePrivate,
      include_write: s.includeWrite ?? false,
    }));
    try {
      const created = await apiPost<ServiceAccountMintResponse>(
        `/api/orgs/${orgId}/api-access/service-accounts/`,
        { display_name: displayName, selections: wireSelections }
      );
      principalEntityId = created.principal_entity_id;
    } catch (err) {
      throw new MintCredentialError(
        err instanceof Error ? err.message : String(err),
        undefined,
        err
      );
    }
  }

  try {
    const secret = await attachSecret(orgId, userId, principalEntityId);
    return { principalEntityId, ...secret };
  } catch (err) {
    throw new MintCredentialError(
      err instanceof Error ? err.message : String(err),
      principalEntityId,
      err
    );
  }
}

export * from './api-access-crud-delete';
